import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type Dispatch,
	type SetStateAction,
} from 'react'
import type { ClientDetailData } from './useClientDetail'
import {
	availableFYYearsFromCachedData,
	buildTimesheetFilterOptionsFromCache,
	calculateProjectStatsFromEntries,
	computeConsolidatedStatsFromCacheSlices,
	computeExpensesFromEntries,
	type ClientInvoiceByPeriodJson,
	type ClientProjectMappingRow,
	type ClientRevenueTrendRow,
	type ConsolidatedStats,
	type GoldFactProjectUserMonthRow,
	type GoldFactSortColumn,
	entryMatchesWorkPeriodFiltersSlices,
	sortGoldFactRows,
} from '../utils/clientTimesheetProjectsUtils'
import { loadRateConfig, getRateForPeriod, type RateConfig } from '../utils/rateConfig'

const BATCH_SIZE = 10

export interface UseClientTimesheetProjectsResult {
	clientId: number | string | undefined
	projects: ClientProjectMappingRow[]
	loading: boolean
	error: string | null
	loadingConsolidated: boolean
	loadingProjectData: boolean
	cachedProjectData: { projectId: number; data: GoldFactProjectUserMonthRow[] }[]
	consolidatedStats: ConsolidatedStats | null
	rateConfig: RateConfig | null
	gradeTechRateByGradeTech: Record<string, number>
	clientInvoiceByPeriod: ClientInvoiceByPeriodJson | null
	clientRevenueTrendRows: ClientRevenueTrendRow[]
	selectedProjectId: number | null
	projectData: GoldFactProjectUserMonthRow[] | null
	filterCalYears: string[]
	setFilterCalYears: Dispatch<SetStateAction<string[]>>
	filterCalMonths: string[]
	setFilterCalMonths: Dispatch<SetStateAction<string[]>>
	filterFYStarts: string[]
	setFilterFYStarts: Dispatch<SetStateAction<string[]>>
	filterFYQuarters: string[]
	setFilterFYQuarters: Dispatch<SetStateAction<string[]>>
	showFilterPanel: boolean
	setShowFilterPanel: (v: boolean | ((p: boolean) => boolean)) => void
	sortColumn: GoldFactSortColumn
	sortDirection: 'asc' | 'desc'
	handleSort: (column: GoldFactSortColumn) => void
	displayCount: number
	setDisplayCount: (v: number | ((n: number) => number)) => void
	loadMoreDetail: () => void
	clearAllFilters: () => void
	clearPeriodFiltersOnly: () => void
	filterOptions: ReturnType<typeof buildTimesheetFilterOptionsFromCache>
	selectProject: (projectId: number) => Promise<void>
	closeProjectDetail: () => void
	filteredDetailRows: GoldFactProjectUserMonthRow[]
	sortedDetailRows: GoldFactProjectUserMonthRow[]
	projectDetailStats: ReturnType<typeof calculateProjectStatsFromEntries>
	projectDetailExpenses: { totalExpenses: number | null; costOfNonBillable: number | null }
	projectDataError: string | null
}

export function useClientTimesheetProjects(
	data: ClientDetailData,
	options?: { disableAutoFYDefault?: boolean },
): UseClientTimesheetProjectsResult {
	const disableAutoFYDefault = options?.disableAutoFYDefault === true
	const clientId = data?.contact_profile?.basic_info?.contact_id

	const [projects, setProjects] = useState<ClientProjectMappingRow[]>([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [loadingConsolidated, setLoadingConsolidated] = useState(false)
	const [loadingProjectData, setLoadingProjectData] = useState(false)
	const [projectDataError, setProjectDataError] = useState<string | null>(null)
	const [cachedProjectData, setCachedProjectData] = useState<
		{ projectId: number; data: GoldFactProjectUserMonthRow[] }[]
	>([])

	const [rateConfig, setRateConfig] = useState<RateConfig | null>(null)
	const [gradeTechRateByGradeTech, setGradeTechRateByGradeTech] = useState<
		Record<string, number>
	>({})
	const [clientInvoiceByPeriod, setClientInvoiceByPeriod] =
		useState<ClientInvoiceByPeriodJson | null>(null)
	const [clientRevenueTrendRows, setClientRevenueTrendRows] = useState<
		ClientRevenueTrendRow[]
	>([])

	const [selectedProjectId, setSelectedProjectId] = useState<number | null>(null)
	const [projectData, setProjectData] = useState<GoldFactProjectUserMonthRow[] | null>(
		null,
	)

	const [filterCalYears, setFilterCalYears] = useState<string[]>([])
	const [filterCalMonths, setFilterCalMonths] = useState<string[]>([])
	const [filterFYStarts, setFilterFYStarts] = useState<string[]>([])
	const [filterFYQuarters, setFilterFYQuarters] = useState<string[]>([])

	const [showFilterPanel, setShowFilterPanel] = useState(true)

	const [sortColumn, setSortColumn] = useState<GoldFactSortColumn>('year-month')
	const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
	const [displayCount, setDisplayCount] = useState(20)

	const timesheetDefaultFYRef = useRef(false)

	useEffect(() => {
		loadRateConfig().then(setRateConfig)
	}, [])

	useEffect(() => {
		let cancelled = false
		fetch('/client_invoice_by_period.json')
			.then((res) => (res.ok ? res.json() : Promise.reject(new Error('fail'))))
			.then((j) => {
				if (!cancelled) setClientInvoiceByPeriod(j as ClientInvoiceByPeriodJson)
			})
			.catch(() => {
				if (!cancelled) setClientInvoiceByPeriod(null)
			})
		return () => {
			cancelled = true
		}
	}, [])

	useEffect(() => {
		let cancelled = false
		fetch('/resource_rate_config/grade_tech_permutations.json')
			.then((res) =>
				res.ok ? res.json() : Promise.reject(new Error('not found')),
			)
			.then((arr: unknown) => {
				if (cancelled || !Array.isArray(arr)) return
				const map: Record<string, number> = {}
				arr.forEach((p: Record<string, unknown>) => {
					const g = p.grade_name != null ? String(p.grade_name).trim() : ''
					const t = p.tech_name != null ? String(p.tech_name).trim() : ''
					if (g && t && p.price != null) map[`${g}|${t}`] = Number(p.price)
				})
				setGradeTechRateByGradeTech(map)
			})
			.catch(() => setGradeTechRateByGradeTech({}))
		return () => {
			cancelled = true
		}
	}, [])

	useEffect(() => {
		timesheetDefaultFYRef.current = false
	}, [clientId])

	useEffect(() => {
		if (!clientId) {
			setClientRevenueTrendRows([])
			return
		}
		let cancelled = false
		const tryFetch = (url: string) =>
			fetch(url).then((res) => (res.ok ? res.json() : Promise.reject(new Error('x'))))
		tryFetch('/json-data/client_revenue_trend.json')
			.catch(() => tryFetch('/data_models/client_revenue_trend.json'))
			.then((arr: unknown) => {
				if (cancelled || !Array.isArray(arr)) return
				const cid = Number(clientId)
				const forClient = arr.filter(
					(r: ClientRevenueTrendRow) => Number(r.contact_id) === cid,
				)
				setClientRevenueTrendRows(forClient)
			})
			.catch(() => setClientRevenueTrendRows([]))
		return () => {
			cancelled = true
		}
	}, [clientId])

	const loadProjectsForClient = useCallback(async () => {
		if (!clientId) {
			setLoading(false)
			setError('Client ID not available')
			setProjects([])
			return
		}
		setLoading(true)
		setError(null)
		try {
			const mappingResponse = await fetch(
				'/ts_project_json/client_project/client_projects_mapping_info.json',
			)
			if (!mappingResponse.ok) {
				if (mappingResponse.status === 404) {
					throw new Error(
						'Timesheet project mapping file not found. Ensure ts_project_json is available under public.',
					)
				}
				throw new Error(`Failed to load project mapping: ${mappingResponse.status}`)
			}
			const ct = mappingResponse.headers.get('content-type')
			if (!ct || !ct.includes('application/json')) {
				const text = await mappingResponse.text()
				if (text.trim().startsWith('<!DOCTYPE') || text.trim().startsWith('<html')) {
					throw new Error(
						'Received HTML instead of JSON for project mapping. Check public/ts_project_json.',
					)
				}
				throw new Error(`Unexpected content type: ${ct}`)
			}
			const mappingData = (await mappingResponse.json()) as ClientProjectMappingRow[]
			const clientProjects = mappingData.filter((project) => {
				const pid = project.client_id
				return pid != null && String(pid) === String(clientId)
			})
			setProjects(clientProjects)
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e)
			setError(msg)
			setProjects([])
		} finally {
			setLoading(false)
		}
	}, [clientId])

	useEffect(() => {
		loadProjectsForClient()
	}, [loadProjectsForClient])

	const loadConsolidatedStats = useCallback(async () => {
		if (projects.length === 0) return
		setLoadingConsolidated(true)
		try {
			const allProjectsData: {
				projectId: number
				data: GoldFactProjectUserMonthRow[]
			}[] = []
			for (let i = 0; i < projects.length; i += BATCH_SIZE) {
				const batch = projects.slice(i, i + BATCH_SIZE)
				const promises = batch.map(async (project) => {
					const pid = project.project_id
					try {
						const response = await fetch(
							`/ts_project_json/gold_fact_project_user_monthly_project_id_${pid}.json`,
						)
						if (!response.ok) return null
						const jsonData = (await response.json()) as unknown
						return {
							projectId: pid,
							data: Array.isArray(jsonData) ? jsonData : [],
						}
					} catch {
						return null
					}
				})
				const results = await Promise.all(promises)
				allProjectsData.push(
					...results.filter(
						(x): x is { projectId: number; data: GoldFactProjectUserMonthRow[] } =>
							x != null,
					),
				)
			}
			setCachedProjectData(allProjectsData)
		} catch (err) {
			console.error('loadConsolidatedStats', err)
		} finally {
			setLoadingConsolidated(false)
		}
	}, [projects])

	useEffect(() => {
		if (projects.length > 0) loadConsolidatedStats()
	}, [projects, loadConsolidatedStats])

	const consolidatedStats = useMemo(() => {
		if (!cachedProjectData.length) return null
		const defaultRate = getRateForPeriod(rateConfig, null, null)
		return computeConsolidatedStatsFromCacheSlices(
			cachedProjectData,
			filterCalYears,
			filterCalMonths,
			filterFYStarts,
			filterFYQuarters,
			gradeTechRateByGradeTech,
			defaultRate,
		)
	}, [
		cachedProjectData,
		filterCalYears,
		filterCalMonths,
		filterFYStarts,
		filterFYQuarters,
		gradeTechRateByGradeTech,
		rateConfig,
	])

	const availableFYYearsFromCache = useMemo(
		() => availableFYYearsFromCachedData(cachedProjectData),
		[cachedProjectData],
	)

	useEffect(() => {
		if (disableAutoFYDefault) return
		if (
			availableFYYearsFromCache.length > 0 &&
			!timesheetDefaultFYRef.current
		) {
			timesheetDefaultFYRef.current = true
			const first = String(availableFYYearsFromCache[0] ?? '')
			const start = parseInt(first.split('-')[0].replace(/^FY\s*/i, ''), 10)
			if (!Number.isNaN(start)) {
				setFilterFYStarts([String(start)])
			}
		}
	}, [availableFYYearsFromCache, disableAutoFYDefault])

	const filterOptions = useMemo(
		() => buildTimesheetFilterOptionsFromCache(cachedProjectData),
		[cachedProjectData],
	)

	const selectProject = useCallback(async (projectId: number) => {
		setLoadingProjectData(true)
		setProjectDataError(null)
		setProjectData(null)
		try {
			const response = await fetch(
				`/ts_project_json/gold_fact_project_user_monthly_project_id_${projectId}.json`,
			)
			if (!response.ok) {
				throw new Error(`Project data not found (${response.status})`)
			}
			const ct = response.headers.get('content-type')
			if (!ct || !ct.includes('application/json')) {
				throw new Error('Invalid response for project JSON')
			}
			const jsonData = (await response.json()) as unknown
			setProjectData(Array.isArray(jsonData) ? jsonData : [])
			setSelectedProjectId(projectId)
			setDisplayCount(20)
			setSortColumn('year-month')
			setSortDirection('desc')
		} catch (e) {
			const msg = e instanceof Error ? e.message : String(e)
			setProjectData(null)
			setProjectDataError(msg)
		} finally {
			setLoadingProjectData(false)
		}
	}, [])

	const closeProjectDetail = useCallback(() => {
		setSelectedProjectId(null)
		setProjectData(null)
		setProjectDataError(null)
		setDisplayCount(20)
		setSortColumn('year-month')
		setSortDirection('desc')
	}, [])

	const filteredDetailRows = useMemo(() => {
		if (!projectData?.length) return []
		return projectData.filter((entry) =>
			entryMatchesWorkPeriodFiltersSlices(
				entry,
				filterCalYears,
				filterCalMonths,
				filterFYStarts,
				filterFYQuarters,
			),
		)
	}, [
		projectData,
		filterCalYears,
		filterCalMonths,
		filterFYStarts,
		filterFYQuarters,
	])

	const sortedDetailRows = useMemo(
		() => sortGoldFactRows(filteredDetailRows, sortColumn, sortDirection),
		[filteredDetailRows, sortColumn, sortDirection],
	)

	const projectDetailStats = useMemo(
		() => calculateProjectStatsFromEntries(filteredDetailRows),
		[filteredDetailRows],
	)

	const projectDetailExpenses = useMemo(() => {
		if (!filteredDetailRows.length) {
			return { totalExpenses: null as number | null, costOfNonBillable: null as number | null }
		}
		const defaultRate = getRateForPeriod(rateConfig, null, null)
		return computeExpensesFromEntries(
			filteredDetailRows,
			gradeTechRateByGradeTech,
			defaultRate,
		)
	}, [filteredDetailRows, gradeTechRateByGradeTech, rateConfig])

	const handleSort = useCallback((column: GoldFactSortColumn) => {
		setSortColumn((prev) => {
			if (prev === column) {
				setSortDirection((d) => (d === 'asc' ? 'desc' : 'asc'))
				return prev
			}
			setSortDirection(column === 'year-month' ? 'desc' : 'asc')
			return column
		})
		setDisplayCount(20)
	}, [])

	const clearAllFilters = useCallback(() => {
		setFilterCalYears([])
		setFilterCalMonths([])
		setFilterFYStarts([])
		setFilterFYQuarters([])
	}, [])

	const clearPeriodFiltersOnly = useCallback(() => {
		setFilterCalYears([])
		setFilterCalMonths([])
		setFilterFYStarts([])
		setFilterFYQuarters([])
	}, [])

	const loadMoreDetail = useCallback(() => {
		const remaining = sortedDetailRows.length - displayCount
		setDisplayCount((prev) => prev + Math.min(50, Math.max(0, remaining)))
	}, [sortedDetailRows.length, displayCount])

	return {
		clientId,
		projects,
		loading,
		error,
		loadingConsolidated,
		loadingProjectData,
		cachedProjectData,
		consolidatedStats,
		rateConfig,
		gradeTechRateByGradeTech,
		clientInvoiceByPeriod,
		clientRevenueTrendRows,
		selectedProjectId,
		projectData,
		filterCalYears,
		setFilterCalYears,
		filterCalMonths,
		setFilterCalMonths,
		filterFYStarts,
		setFilterFYStarts,
		filterFYQuarters,
		setFilterFYQuarters,
		showFilterPanel,
		setShowFilterPanel,
		sortColumn,
		sortDirection,
		handleSort,
		displayCount,
		setDisplayCount,
		loadMoreDetail,
		clearAllFilters,
		clearPeriodFiltersOnly,
		filterOptions,
		selectProject,
		closeProjectDetail,
		filteredDetailRows,
		sortedDetailRows,
		projectDetailStats,
		projectDetailExpenses,
		projectDataError,
	}
}
